import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { COMPANY, SERVICE_AREAS, FORMSPREE_URL } from "@/data/content";
import { CITY_LIST } from "@/data/cities";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const fsRes = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ name, email, _subject: "New Newsletter Signup — HoneyBee Roofers", type: "newsletter" }),
      });
      if (!fsRes.ok) throw new Error("submission_failed");
      api.post("/newsletter", { name, email }).catch(() => {});
      toast.success("You're in the hive! Thanks for subscribing.");
      setEmail(""); setName("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize the RealWorkLabs "neighborhood" widget sitewide (renders into #rwl-neighborhood).
    const init = () => {
      try {
        if (window.rwlPlugin && typeof window.rwlPlugin.init === "function") {
          window.rwlPlugin.init("https://app.realworklabs.com", "zkjtV-AXoisfymKI");
        }
      } catch (e) { /* no-op */ }
    };
    if (window.rwlPlugin) init();
    else window.addEventListener("rwlPluginReady", init, false);
    return () => window.removeEventListener("rwlPluginReady", init, false);
  }, []);

  return (
    <footer className="bg-carbon border-t border-white/10 pt-16 pb-28 lg:pb-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* RealWorkLabs neighborhood widget (renders sitewide when populated) */}
        <div id="rwl-neighborhood" className="empty:hidden mb-12"></div>
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <img src="/honeybee-logo-light.svg" alt="Honey Bee Roofing & Exteriors" className="h-16 w-auto" />
            <p className="text-sm text-zinc-400 mt-5 max-w-sm leading-relaxed">
              Locally owned roofing & exteriors based in Everett, WA, serving Snohomish, King & Skagit County and the greater Puget Sound.
              Honest, flat-rate pricing backed by a 50-year CertainTeed guarantee.
            </p>
            <div className="space-y-2.5 mt-6 text-sm">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 text-zinc-300 hover:text-honey transition-colors">
                <Phone className="w-4 h-4 text-honey" /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-zinc-300 hover:text-honey transition-colors">
                <Mail className="w-4 h-4 text-honey" /> {COMPANY.email}
              </a>
              <a href="https://maps.google.com/?q=118+SE+Everett+Mall+Way+Everett+WA+98208" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-zinc-400 hover:text-honey transition-colors" data-testid="footer-address">
                <MapPin className="w-4 h-4 text-honey mt-0.5 shrink-0" />
                <span>{COMPANY.address.street}<br />{COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.zip}</span>
              </a>
              <p className="flex items-center gap-3 text-zinc-400"><Clock className="w-4 h-4 text-honey" /> {COMPANY.hours}</p>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-display font-bold text-white uppercase tracking-wider mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              {["Residential Roof Replacement", "Storm Damage Repair", "Roof Leak Repair", "Roof Inspection", "Siding & Windows", "Decks & Outdoor Living"].map((l) => (
                <li key={l}><a href="/#services" className="hover:text-honey transition-colors">{l}</a></li>
              ))}
              <li><Link to="/commercial-roofing" data-testid="footer-commercial" className="hover:text-honey transition-colors">Commercial Roofing</Link></li>
              <li><Link to="/insurance-roof-replacement" data-testid="footer-insurance" className="hover:text-honey transition-colors">Insurance Roof Claims</Link></li>
              <li><Link to="/recent-projects" data-testid="footer-projects" className="hover:text-honey transition-colors">Recent Projects</Link></li>
              <li><Link to="/blog" data-testid="footer-blog" className="hover:text-honey transition-colors">Roofing Blog</Link></li>
            </ul>
          </div>

          {/* Areas */}
          <div className="lg:col-span-2">
            <h4 className="text-sm font-display font-bold text-white uppercase tracking-wider mb-4">Service Area</h4>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              {CITY_LIST.map((c) => (
                <li key={c.slug}>
                  <Link to={`/roofing/${c.slug}`} className="hover:text-honey transition-colors" data-testid={`footer-city-${c.slug}`}>
                    Roofing in {c.name}, {c.state}
                  </Link>
                </li>
              ))}
              {SERVICE_AREAS.slice(0, 5).map((a) => (
                <li key={a} className="text-zinc-500">{a}</li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-3">
            <h4 className="text-sm font-display font-bold text-white uppercase tracking-wider mb-4">Join the Hive</h4>
            <p className="text-sm text-zinc-400 mb-4">Seasonal tips to keep your Washington roof in top shape. No spam — buzz off anytime.</p>
            <form onSubmit={subscribe} className="space-y-2" data-testid="newsletter-form">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name" data-testid="newsletter-name"
                className="w-full bg-black/40 border border-white/15 rounded-sm px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-honey" />
              <div className="flex gap-2">
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="newsletter-email"
                  className="flex-1 bg-black/40 border border-white/15 rounded-sm px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-honey" />
                <button type="submit" disabled={loading} data-testid="newsletter-submit"
                  className="bg-honey text-white px-4 rounded-sm hover:bg-honey-600 transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">© {new Date().getFullYear()} {COMPANY.name}. Licensed &amp; insured in Washington State. {COMPANY.license}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="text-xs text-zinc-500 hover:text-honey transition-colors">Privacy Policy</Link>
            <Link to="/terms-and-conditions" data-testid="footer-terms" className="text-xs text-zinc-500 hover:text-honey transition-colors">Terms &amp; Conditions</Link>
            <a href="/admin/login" data-testid="admin-link" className="text-xs text-zinc-600 hover:text-honey transition-colors">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
